import api from './apiService';
import { getApiUrl } from '../config';
import { Recipe, RecipeTriggerType } from '../types/recipe';
import { getEffectiveAuthToken, recoverFromAuthenticationFailure } from './authToken';

export type RecipeDraftJobStatus = 'queued' | 'preparing' | 'generating' | 'succeeded' | 'failed';

export interface RecipeDraftJob {
  id: string;
  recipeId: number;
  conversationId: string;
  status: RecipeDraftJobStatus;
  statusText: string | null;
  partialContent: string;
  proposedName: string | null;
  proposedTriggerType: RecipeTriggerType | null;
  chefJobId: string | null;
  errorMessage: string | null;
  attempt: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export const startRecipeAuthoring = async (description: string): Promise<{
  recipe: Recipe;
  conversationId: string;
  draftJobId: string;
}> => {
  const response = await api.post<{
    recipe: Recipe;
    conversationId: string;
    draftJobId: string;
  }>(getApiUrl('recipes/authoring'), { description });
  return response.data;
};

export const createBlankRecipe = async (input?: {
  name?: string;
  content?: string;
  triggerType?: RecipeTriggerType;
  ensureUniqueName?: boolean;
}): Promise<Recipe> => {
  const response = await api.post<Recipe>(getApiUrl('recipes/blank'), input || {});
  return response.data;
};

export const getLatestRecipeDraft = async (recipeId: number): Promise<RecipeDraftJob | null> => {
  const response = await api.get<RecipeDraftJob | null>(getApiUrl(`recipes/${recipeId}/draft-job`));
  return response.data;
};

export const retryRecipeDraft = async (jobId: string): Promise<RecipeDraftJob> => {
  const response = await api.post<RecipeDraftJob>(getApiUrl(`recipes/draft-jobs/${jobId}/retry`));
  return response.data;
};

export const observeRecipeDraft = (
  jobId: string,
  callbacks: {
    onSnapshot: (job: RecipeDraftJob) => void;
    onDelta: (partialContent: string) => void;
    onComplete: (job: RecipeDraftJob) => void;
    onError: (error: Error, job?: RecipeDraftJob) => void;
  }
): (() => void) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', getApiUrl(`recipes/draft-jobs/${jobId}/stream`), true);
  const token = getEffectiveAuthToken();
  if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  xhr.responseType = 'text';
  let processedLength = 0;
  let leftover = '';
  let terminal = false;
  let aborted = false;

  const handle = (eventName: string, data: any) => {
    if (eventName === 'snapshot') callbacks.onSnapshot(data as RecipeDraftJob);
    if (eventName === 'delta' && typeof data.partialContent === 'string') {
      callbacks.onDelta(data.partialContent);
    }
    if (eventName === 'complete') {
      terminal = true;
      callbacks.onComplete(data as RecipeDraftJob);
    }
    if (eventName === 'error') {
      terminal = true;
      callbacks.onError(new Error(data.error || 'Chef could not draft the recipe.'), data.job);
    }
  };

  xhr.onprogress = () => {
    const incoming = xhr.responseText.substring(processedLength);
    processedLength = xhr.responseText.length;
    let buffer = (leftover + incoming).replace(/\r\n/g, '\n');
    let boundary: number;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const raw = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const lines = raw.split('\n');
      const eventName = lines.find(line => line.startsWith('event:'))?.replace(/^event:\s*/, '').trim() || 'message';
      const dataLine = lines.find(line => line.startsWith('data:'));
      if (!dataLine) continue;
      try {
        handle(eventName, JSON.parse(dataLine.replace(/^data:\s*/, '').trim()));
      } catch (error) {
        console.debug('Could not parse recipe draft event', error);
      }
    }
    leftover = buffer;
  };
  xhr.onerror = () => {
    if (!terminal && !aborted) callbacks.onError(new Error('Live recipe updates were interrupted. Chef is still working.'));
  };
  xhr.onloadend = () => {
    if (terminal || aborted) return;
    if (xhr.status === 401) {
      recoverFromAuthenticationFailure();
      return;
    }
    callbacks.onError(new Error('Live recipe updates ended. Reload to reconnect.'));
  };
  xhr.send();
  return () => {
    if (xhr.readyState !== 4) {
      aborted = true;
      xhr.abort();
    }
  };
};
